import React, { useEffect, useRef } from "react";
import { complianceChipClass, fallbackDisagreementLabel, fallbackExplanation } from "./RfpComplianceChip";

/** Split App 3 "[3]: filename — heading, p. 1" into marker + source label. */
export function parseResolvedCitation(line) {
  const text = String(line || "").trim();
  const match = text.match(/^(\[\d+\])\s*:\s*(.*)$/);
  if (match) return { marker: match[1], source: match[2] };
  return { marker: "", source: text };
}

export default function RfpComplianceDrawer({
  row,
  result,
  checking,
  onClose,
  onRecheck,
}) {
  const ref = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!row) return null;

  const chip = result?.chip || "";
  const resolved = Array.isArray(result?.citations_resolved)
    ? result.citations_resolved.map(parseResolvedCitation).filter((item) => item.source)
    : [];

  return (
    <div className="fixed inset-0 z-[90] flex justify-end bg-black/40" onClick={onClose} data-testid="rfp-compliance-drawer">
      <div
        ref={ref}
        className="flex h-full w-full max-w-md flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <h3 className="font-semibold">Compliance · {row.id}</h3>
          <button type="button" className="text-slate-500 hover:text-slate-800 dark:hover:text-slate-200" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 text-sm">
          <dl className="space-y-3">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Verdict</dt>
              <dd className="mt-1">
                {chip ? (
                  <span className={`inline-block rounded-full border px-2.5 py-1 text-xs font-semibold ${complianceChipClass(chip)}`}>
                    {chip}
                  </span>
                ) : (
                  <span className="text-slate-400">Not checked</span>
                )}
              </dd>
            </div>
            {row.technology ? (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Product</dt>
                <dd>{row.technology}</dd>
              </div>
            ) : null}
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Requirement</dt>
              <dd className="whitespace-pre-wrap leading-relaxed text-slate-700 dark:text-slate-200">{row.text || "—"}</dd>
            </div>
            {result?.explanation ? (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Explanation</dt>
                <dd className="whitespace-pre-wrap leading-relaxed text-slate-700 dark:text-slate-200">{result.explanation}</dd>
              </div>
            ) : null}
            {resolved.length > 0 ? (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Sources</dt>
                <dd>
                  <ul className="mt-1 space-y-1.5" data-testid="rfp-compliance-sources">
                    {resolved.map((item, idx) => (
                      <li key={`${item.marker}-${idx}`} className="leading-snug text-slate-700 dark:text-slate-200">
                        {item.marker ? (
                          <span className="mr-1.5 rounded bg-slate-100 px-1 py-0.5 font-mono text-xs dark:bg-slate-800">
                            {item.marker}
                          </span>
                        ) : null}
                        <span>{item.source}</span>
                      </li>
                    ))}
                  </ul>
                </dd>
              </div>
            ) : null}
            {result?.error_message ? (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Error</dt>
                <dd className="whitespace-pre-wrap text-rose-700 dark:text-rose-300">{result.error_message}</dd>
              </div>
            ) : null}
            {result?.fallback_action ? (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Second pass</dt>
                <dd className="space-y-1">
                  {fallbackDisagreementLabel(chip, result.fallback_action) ? (
                    <p>
                      First pass: Non-compliant · Second pass:{" "}
                      {fallbackDisagreementLabel(chip, result.fallback_action)}
                    </p>
                  ) : null}
                  <p className="whitespace-pre-wrap leading-relaxed text-slate-700 dark:text-slate-200">
                    {fallbackExplanation(result.fallback_action)}
                  </p>
                </dd>
              </div>
            ) : null}
          </dl>
        </div>
        <div className="border-t border-slate-200 p-4 dark:border-slate-700">
          <button
            type="button"
            disabled={checking}
            className="w-full rounded-lg bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-60"
            onClick={() => onRecheck?.(row.id)}
          >
            {checking ? "Checking…" : "Re-check this row"}
          </button>
        </div>
      </div>
    </div>
  );
}
